import { useState, useRef, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { ImportError, ImportMappingsResult } from '@/api/types/appraisalCycle.types';

const ACCEPTED_EXTENSIONS = '.xlsx';
const ACCEPTED_LABEL = '.xlsx';
const FILE_TYPE_ERROR = 'Only .xlsx files are supported.';

function isXlsx(file: File): boolean {
  return file.name.toLowerCase().endsWith('.xlsx');
}

interface ImportExcelDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<ImportMappingsResult>;
  isImporting: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ImportExcelDialog({ open, onClose, onImport, isImporting }: ImportExcelDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setIsDragOver(false);
    setFileError(null);
    setImportErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const selectFile = (file: File) => {
    setImportErrors([]);
    setSelectedFile(file);
    setFileError(isXlsx(file) ? null : FILE_TYPE_ERROR);
  };

  const handleClose = () => {
    if (isImporting) return;
    resetState();
    onClose();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      selectFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      selectFile(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChooseFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
    setImportErrors([]);
  };

  const handleImport = async () => {
    if (!selectedFile || fileError) return;
    const result = await onImport(selectedFile);
    if (result.success) {
      resetState();
    } else {
      setImportErrors(result.errors ?? []);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import excel</DialogTitle>
      <DialogContent>
        {!selectedFile ? (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : 'grey.400',
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragOver ? 'action.hover' : 'transparent',
              transition: 'all 0.2s ease',
            }}
            onClick={handleChooseFileClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleChooseFileClick(); } }}
            aria-label="Upload file drop zone"
          >
            <UploadFileIcon sx={{ fontSize: 40, color: 'grey.500', mb: 1 }} />
            <Typography variant="body1">
              Drag & Drop or{' '}
              <Typography component="span" sx={{ color: 'secondary.main', cursor: 'pointer', fontWeight: 500 }}>
                Choose file
              </Typography>
              {' '}to upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ACCEPTED_LABEL}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1">{selectedFile.name}</Typography>
              <Typography variant="body2" color="text.secondary">{formatFileSize(selectedFile.size)}</Typography>
            </Box>
            <IconButton onClick={handleRemoveFile} disabled={isImporting} size="small" aria-label="Remove file">
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={ACCEPTED_EXTENSIONS}
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: 'none' }}
        />
        {fileError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {fileError}
          </Alert>
        )}
        {importErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Import errors:</Typography>
            {importErrors.slice(0, 5).map((err, idx) => (
              <Typography key={idx} variant="body2">
                Row {err.row}: {err.field} "{err.value}" - {err.reason}
              </Typography>
            ))}
            {importErrors.length > 5 && (
              <Typography variant="body2">...and {importErrors.length - 5} more errors</Typography>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isImporting}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          color="secondary"
          disabled={!selectedFile || isImporting || !!fileError}
          startIcon={isImporting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
